import { useTranslation, withTranslation } from "react-i18next";
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { getAllBusinesses, changePage, select, searchBusinesses, deleteBusinesses } from 'store/slices/adminSlice';
import { PagingState, SelectionState, IntegratedSelection, CustomPaging, DataTypeProvider } from '@devexpress/dx-react-grid';
import { Grid, Table, TableHeaderRow, TableSelection, PagingPanel } from '@devexpress/dx-react-grid-bootstrap4';
import Loading from '../Loading';
import { Row, Col } from 'reactstrap';
import BusinessState from './Business/BusinessState';
import InteractiveName from './Business/InteractiveName';
import DeleteResourcesButton from './DeleteResourcesButton'; // Show business id linking to business page (truncated with hover expansion)

const BusinessLinkFormatter = ({
  value: id
}) => {
  const {
    // eslint-disable-next-line no-unused-vars
    t
  } = useTranslation();
  if (!id) return <span className="text-muted">-</span>;
  return <Link to={`/admin/business/${id}`} title={id}>
      <code>{id}</code>
    </Link>;
}; // Format date nicely


const DateFormatter = ({
  value
}) => {
  const {
    // eslint-disable-next-line no-unused-vars
    t
  } = useTranslation();
  if (!value) return <span className="text-muted">-</span>;
  const dateVal = typeof value === 'object' && value.iso ? value.iso : value;
  const d = new Date(dateVal);
  return <span title={d.toLocaleString()} style={{
    fontSize: '0.85rem'
  }}>
      {d.toLocaleDateString()}
    </span>;
}; // Show field value linking to user page (safe check for null owner)


const UserLinkFormatter = ({
  value
}) => {
  const {
    // eslint-disable-next-line no-unused-vars
    t
  } = useTranslation();
  if (!value) return <span className="text-muted">-</span>;
  const {
    id,
    fullName
  } = value;
  return <Link to={`/admin/user/${id}`}>{fullName || 'N/A'}</Link>;
}; // Show status field value and control


const StatusFormatter = ({
  row
}) => {
  const {
    // eslint-disable-next-line no-unused-vars
    t
  } = useTranslation();
  return <BusinessState business={row} />;
}; // Show domain field with validation highlight


const DomainFormatter = ({
  value: domain
}) => {
  const {
    // eslint-disable-next-line no-unused-vars
    t
  } = useTranslation();

  if (!domain) {
    return <span className='text-danger'>-</span>;
  }

  return <a href={domain} target="_blank" rel="noopener noreferrer" className="text-primary">{domain}</a>;
}; // Show name field with validation control


const NameFormatter = ({
  value: name,
  row
}) => {
  const {
    // eslint-disable-next-line no-unused-vars
    t
  } = useTranslation();

  if (!name) {
    return <span className='text-danger'>-</span>;
  }

  return <InteractiveName name={name} businessId={row?.id} />;
}; // Main All Businesses table


export class AllBusinesses extends Component {
  constructor(props) {
    super(props);
    this.state = {
      columns: [{
        name: 'id',
        title: 'Business id'
      }, {
        name: 'name',
        title: 'Name'
      }, {
        name: 'website',
        title: 'Website'
      }, {
        name: 'phone',
        title: 'Phone'
      }, {
        name: 'locations',
        title: 'Locations'
      }, {
        name: 'createdAt',
        title: 'Created At'
      }]
    };
    this.deleteBusinesses = this.deleteBusinesses.bind(this);
  }

  componentDidMount() {
    this.props.getAllBusinesses();
  }

  deleteBusinesses() {
    return this.props.deleteBusinesses(this.props.data.selection);
  }

  domain(business) {
    return business && business.website;
  }

  render() {
    const { t } = this.props;
    const {
      loading = false,
      rows = [],
      page: currentPage = 0,
      perPage: pageSize = 10,
      total = 0,
      selection = [],
      error,
      deleteError
    } = this.props.data || {};
    const {
      changeCurrentPage,
      onSelectionChange
    } = this.props;
    const {
      columns
    } = this.state;
    return <>
        <Row>
          <Col>
            <h4 className="mb-4" style={{
            fontSize: '1.5rem',
            textTransform: 'none',
            letterSpacing: 'normal',
            fontWeight: 600,
            color: 'white'
          }}>{t("List of")}{loading && !total ? '' : total}{t("businesses")}</h4>
          </Col>
          <Col>
            {
            /* <Input type='text' name='search' id='filterBusinesses'
             placeholder='Filter businesses...'
             value={search}
             onChange={e => {
               onSelectionChange([])
               searchBusinesses(e.target.value)
             }}
            /> */
          }
          </Col>
        </Row>
        {error && <Row>
            <Col>
              <p className='text-danger twice'>
                {error && error.message}
              </p>
            </Col>
          </Row>}
        <div className='position-relative'>
          <Grid rows={rows} columns={columns} getRowId={row => row.id}>
            <PagingState currentPage={currentPage} onCurrentPageChange={changeCurrentPage} pageSize={pageSize} />
            <SelectionState selection={selection} onSelectionChange={onSelectionChange} />

            <CustomPaging totalCount={total} />
            <IntegratedSelection />

            <DataTypeProvider formatterComponent={BusinessLinkFormatter} for={['id']} />

            <DataTypeProvider formatterComponent={DateFormatter} for={['createdAt']} />

            <DataTypeProvider formatterComponent={StatusFormatter} for={['state']} />

            <DataTypeProvider formatterComponent={UserLinkFormatter} for={['owner']} />

            <DataTypeProvider formatterComponent={DomainFormatter} for={['website']} />

            <DataTypeProvider formatterComponent={NameFormatter} for={['name']} />

            <Table />

            <TableHeaderRow />
            <PagingPanel />
            <TableSelection showSelectAll highlightRow selectByRowClick />
          </Grid>
          {loading && <Loading />}
        </div>
        {selection.length > 0 && <Row>
            <Col>
              <p className='twice'>{t("Selected:")}{selection.length}{t("row")}{selection.length !== 1 && 's'}</p>
              <DeleteResourcesButton resourceName='business accounts' rows={selection} deleteResources={this.deleteBusinesses} loading={loading} error={deleteError} />
            </Col>
          </Row>}
      </>;
  }

}

const mapStateToProps = state => ({
  data: state.admin.businesses
});

const mapDispatchToProps = dispatch => ({
  getAllBusinesses: () => dispatch(getAllBusinesses()),
  changeCurrentPage: currentPage => dispatch(changePage(currentPage)),
  onSelectionChange: selection => dispatch(select(selection)),
  searchBusinesses: search => dispatch(searchBusinesses(search)),
  deleteBusinesses: businessIds => dispatch(deleteBusinesses(businessIds))
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(AllBusinesses));